import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { logoutUser } from '../../redux/thunk/authThunk';
import { updateSettings } from '../../redux/slices/settingsSlice';
import { setActiveTab } from '../../redux/slices/appSlice';
import { openReportModal } from '../../redux/slices/reportSlice';
import { savePreferences } from '../../redux/thunk/settingsThunk';
import { NAV_OVERLAY_EVENT } from '../NotificationBell';
import { resolveTheme } from '../../utils/themeResolver';
import { userInitials, userDisplayName } from '../../utils/userDisplay';
import { track } from '../../utils/analytics';
import ClickableDiv from '../common/ClickableDiv';
import { formatTimeAgo } from '../../utils/cloudBackup';
import './UserMenu.scss';

const SUBSCRIPTION_COLORS = {
    free: '#888',
    pro: '#00bcd4',
    goat: '#FFD700',
};

const UserMenu = () => {
    const dispatch = useDispatch();
    const { user, isAuthenticated } = useSelector((state) => state.auth);
    const theme = useSelector((state) => state.settings.theme);
    const [open, setOpen] = useState(false);
    const menuRef = useRef(null);

    // Compact Cloud Backup status for the menu row (desktop only). Refreshed
    // each time the menu opens — cheap (cached usage server-side) and always
    // current when the user actually looks.
    const backupApi = typeof window !== 'undefined' ? window.electron?.backup : null;
    const [backupLine, setBackupLine] = useState(null);
    useEffect(() => {
        if (!open || !backupApi) return;
        backupApi.getStatus().then((s) => {
            if (!s || s.error) return;
            if (s.snapshotRunning) { setBackupLine('Backing up…'); return; }
            if (!s.settings?.enabled) { setBackupLine('Off'); return; }
            const newest = Math.max(0, ...Object.values(s.settings.lastSnapshot || {}).map((l) => l?.at || 0));
            setBackupLine(newest ? formatTimeAgo(newest) : 'Not yet');
        }).catch(() => {});
    }, [open, backupApi]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Close when another nav overlay announces it's opening
    useEffect(() => {
        const onOtherOpen = (e) => {
            if (e.detail?.source === 'user-menu') return;
            setOpen(false);
        };
        window.addEventListener(NAV_OVERLAY_EVENT, onOtherOpen);
        return () => window.removeEventListener(NAV_OVERLAY_EVENT, onOtherOpen);
    }, []);

    if (!isAuthenticated || !user) return null;

    const initials = userInitials(user);
    const displayName = userDisplayName(user);
    const subscriptionType = (user.subscription_type || 'free').toLowerCase();
    const subscriptionLabel = subscriptionType.charAt(0).toUpperCase() + subscriptionType.slice(1);
    const badgeColor = SUBSCRIPTION_COLORS[subscriptionType] || SUBSCRIPTION_COLORS.free;

    // Quick theme toggle (also available in Settings → Appearance). Theme is a
    // frequent, one-click action, so it earns a spot in the menu; zoom (set
    // once) lives only in Appearance + the View menu.
    const handleToggleTheme = () => {
        const effective = theme === 'auto' ? resolveTheme('auto') : theme;
        const newTheme = effective === 'dark' ? 'light' : 'dark';
        document.documentElement.setAttribute('data-theme', newTheme);
        dispatch(updateSettings({ theme: newTheme }));
        // Server write at the user-intent site (not the settings middleware),
        // so boot-time hydration dispatches don't echo back and race the cache.
        dispatch(savePreferences({ theme: newTheme }));
    };

    const handleManageSubscription = () => {
        setOpen(false);
        dispatch(setActiveTab('subscription'));
    };

    const handleBackup = () => {
        setOpen(false);
        track('backup_page_opened', { from: 'user_menu' });
        dispatch(setActiveTab('backup'));
    };

    const handleSettings = () => {
        setOpen(false);
        track('settings_opened', { from: 'user_menu' });
        dispatch(setActiveTab('settings'));
    };

    const handleAccount = () => {
        setOpen(false);
        dispatch(setActiveTab('account'));
    };

    const handleAbout = () => {
        setOpen(false);
        dispatch(setActiveTab('about'));
    };

    const handleReportProblem = () => {
        setOpen(false);
        dispatch(openReportModal({ source: 'user-menu' }));
    };

    const handleLogout = () => {
        setOpen(false);
        dispatch(logoutUser());
    };

    return (
        <div className="user-menu" ref={menuRef}>
            <ClickableDiv
                className="user-menu-trigger"
                onClick={() => {
                    if (!open) {
                        window.dispatchEvent(new CustomEvent(NAV_OVERLAY_EVENT, { detail: { source: 'user-menu' } }));
                    }
                    setOpen(!open);
                }}
                title={displayName}
            >
                <span className="user-avatar">{initials}</span>
            </ClickableDiv>

            {open && (
                <div className="user-menu-dropdown">
                    <div className="user-menu-header">
                        <div className="user-menu-avatar-large">{initials}</div>
                        <div className="user-menu-info">
                            <span className="user-menu-name">
                                {displayName}
                            </span>
                            <span className="user-menu-email">{user.email}</span>
                        </div>
                    </div>
                    <div className="user-menu-divider" />
                    <div className="user-menu-subscription">
                        <span className="subscription-label">Plan</span>
                        <span
                            className="subscription-badge"
                            style={{
                                color: badgeColor,
                                borderColor: badgeColor,
                            }}
                        >
                            {subscriptionLabel}
                        </span>
                    </div>
                    {subscriptionType === 'goat' && (
                        <ClickableDiv className="user-menu-item" onClick={handleManageSubscription}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="2" y="5" width="20" height="14" rx="2"/>
                                <line x1="2" y1="10" x2="22" y2="10"/>
                            </svg>
                            Manage subscription
                        </ClickableDiv>
                    )}
                    <div className="user-menu-divider" />
                    <div className="user-menu-theme-toggle">
                        <span className="theme-label">Theme</span>
                        <ClickableDiv
                            className="theme-toggle-btn"
                            onClick={handleToggleTheme}
                            title={`Switch to ${resolveTheme(theme) === 'dark' ? 'light' : 'dark'} mode`}
                        >
                            {resolveTheme(theme) === 'dark' ? (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="12" cy="12" r="5"/>
                                    <line x1="12" y1="1" x2="12" y2="3"/>
                                    <line x1="12" y1="21" x2="12" y2="23"/>
                                    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
                                    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                                    <line x1="1" y1="12" x2="3" y2="12"/>
                                    <line x1="21" y1="12" x2="23" y2="12"/>
                                    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
                                    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
                                </svg>
                            ) : (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                                </svg>
                            )}
                        </ClickableDiv>
                    </div>
                    <div className="user-menu-divider" />
                    <ClickableDiv className="user-menu-item" onClick={handleSettings}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="3"/>
                            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                        </svg>
                        Settings
                    </ClickableDiv>
                    <ClickableDiv className="user-menu-item" onClick={handleAccount}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                            <circle cx="12" cy="7" r="4"/>
                        </svg>
                        Account
                    </ClickableDiv>
                    {backupApi && (
                        <ClickableDiv className="user-menu-item" onClick={handleBackup}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M20 17.6A4.5 4.5 0 0 0 17.5 9.4a6 6 0 0 0-11.6 1.6A4 4 0 0 0 6 19h11"/>
                                <path d="M12 12v6M9.5 14.5L12 12l2.5 2.5"/>
                            </svg>
                            Cloud Backup
                            {backupLine && <span className="user-menu-item-status">{backupLine}</span>}
                        </ClickableDiv>
                    )}
                    <div className="user-menu-divider" />
                    <div className="user-menu-section-label">Help &amp; Support</div>
                    <ClickableDiv className="user-menu-item" onClick={handleReportProblem}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                            <line x1="12" y1="9" x2="12" y2="13"/>
                            <line x1="12" y1="17" x2="12.01" y2="17"/>
                        </svg>
                        Report a problem
                    </ClickableDiv>
                    <ClickableDiv className="user-menu-item" onClick={handleAbout}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10"/>
                            <line x1="12" y1="16" x2="12" y2="12"/>
                            <line x1="12" y1="8" x2="12.01" y2="8"/>
                        </svg>
                        About
                    </ClickableDiv>
                    <ClickableDiv className="user-menu-logout" onClick={handleLogout}>
                        Log out
                    </ClickableDiv>
                </div>
            )}
        </div>
    );
};

export default UserMenu;
